export const enSwapUi = {
  connectWallet: 'Connect wallet',
  convertToWeth: 'Convert to WETH',
  convertToEth: 'Convert to ETH',
  convert: 'Convert',
  approved: 'Approved',
  approveToken: (symbol: string) => `Approve ${symbol}`,
  awaitingWalletSignature: 'Waiting for wallet signature',
  awaitingConfirmation: 'Waiting for confirmation',
  cancelWalletAction: 'Cancel operation',
  cancelWalletActionHint:
    'If you closed MetaMask without signing or rejecting, cancel here to unlock the screen. You can try again whenever you want.',
  awaitingConfirmationHint:
    'The transaction was already sent to the network. Waiting for on-chain confirmation only.',
  impactVeryHigh: 'Very high impact',
  impactTooHigh: 'Impact too high',
  swapAnyway: 'Swap anyway',
  swap: 'Swap',
  selectTokenTitle: (field: string) => `Select token — ${field}`,
  field: { pay: 'Pay', receive: 'Receive' },
  selectPairForQuote: 'Select a pair to see the market quote.',
  marketPrice: 'Market price',
  rate: 'Rate',
  minReceived: 'Min. received',
  maxSold: 'Max. sold',
  invalidPair: 'Invalid pair.',
  priceImpact: 'Price impact',
  impact: 'Impact',
  priceImpactGuide: {
    safe: '"Don\'t Panic"',
    caution: '"Mostly Harmless"',
    blocked: '"Beyond Mostly Harmless"',
    limitPair: (pct: string) => `Pair limit: ${pct}%`,
    limitGlobal: (pct: string) => `Global limit: ${pct}%`,
  },
  slippage: 'Slippage',
  confirmReceiveNotice: {
    title: 'Notice about amount to receive',
    body:
      'The estimated amount you receive may be lower or change before the transaction confirms, especially during high volatility or heavy activity on the pair.',
    slippageHint: 'If your transactions revert, consider increasing slippage tolerance in',
    settingsLink: 'Trade settings',
  },
  enterAmountIdle: 'Enter an amount to see rate, minimum received and swap impact.',
  enterAmountIdleShort: 'Enter an amount to see rate, minimum and impact.',
  estimatedLpFee: 'Estimated LP fee:',
  searchPairPlaceholder: 'Search pair…',
  noPairsOnChain: 'No pairs in the catalog for this chain.',
  noPairSearchMatch: 'No pair matches your search.',
  label: {
    from: 'From',
    to: 'To',
    fromEstimated: 'From (estimated)',
    toEstimated: 'To (estimated)',
  },
  error: {
    insufficientLiquidity: 'Insufficient liquidity for this swap.',
    swapDisabled: 'Swapping this token is not available',
    prepareTxFailed: 'Could not prepare transaction. Open MetaMask and try again.',
    revertedOnChain: 'Transaction reverted on-chain.',
  },
  synthesis: {
    connectToDispense: 'Connect wallet to dispense',
    wrapToWeth: 'Convert to WETH',
    unwrapToEth: 'Convert to ETH',
    wrapAvailable: 'Conversion available',
    approvalPending: 'Approval submitted — awaiting confirmation',
    approveToContinue: (symbol: string) => `Approve ${symbol} to continue`,
    impactTooHighDispense: 'Impact too high to dispense',
    enterValidAmounts: 'Enter valid amounts',
    highImpactReview: 'High impact — review display telemetry',
    invalidAmountsForPair: 'Invalid amounts for this pair',
    enterAmountsToDispense: 'Enter amounts to dispense',
    approvalPendingToken: 'Token approval pending',
  },
  readout: {
    syncingQuote: 'Syncing market quote…',
    selectPairInBay: 'Select the token pair in the bay to begin.',
    noPoolAvailable: 'This pair has no available pool. Choose another pair.',
    reviewBeforeConfirm: 'Estimated swap parameters — review before confirming.',
    enterAmount: 'Enter the amount you want to swap.',
  },
  tokenDialog: { pay: 'Pay', receive: 'Receive' },
  entry: {
    poolNotDeployed:
      'This pool is not deployed on-chain yet. You can create liquidity for this pair.',
    addLiquidityCta: 'Add liquidity',
    dismiss: 'Dismiss',
  },
} as const

export const enPoolUi = {
  liquidityPool: 'Liquidity pool',
  vol24h: '24h vol',
  tradeOnNutrimatic: 'Trade on Nutri-Matic',
  reservesInPool: 'Pool reserves',
  composition: 'Pool composition',
  compositionAria: (token0: string, share0: string, token1: string, share1: string) =>
    `Composition ${token0} ${share0}, ${token1} ${share1}`,
  compositionCaptionIndexed: 'Market catalog data (indexer).',
  compositionCaptionOnChain: 'On-chain estimate for a pair outside the indexed catalog.',
  onChainContracts: 'On-chain contracts',
  lpSharesFootnote:
    'In V2 pools, the pair contract mints LP shares when adding liquidity. There is no separate LP token.',
  metric: {
    price: 'Price',
    priceSub: 'spot',
    change24h: '24h change',
    change24hSub: 'price movement',
    tvl: 'TVL',
    tvlSub: 'total value locked',
    volume24hSub: 'spot trades',
    reserve0: 'Reserve 0',
    reserve1: 'Reserve 1',
    feeSub: 'per swap',
    apyEstimated: 'Estimated APY',
    apySub: 'fees + incentives',
    volume7d: '7d volume',
    volume7dSub: 'cumulative',
    trades24h: '24h trades',
    trades24hSub: 'pool swaps',
    tvlComposition: 'TVL composition',
  },
  createdAt: (date: string) => `created ${date}`,
  noPairPriceData: 'No price data for this pair.',
  chartTitle24h: 'Pair price (24h)',
  poolsOfToken: (symbol: string) => `Pools of ${symbol}`,
  ecosystemLiquidityTitle: 'Ecosystem liquidity',
  addLiquidityCta: 'Add liquidity',
} as const

export const enTokenDetail = {
  invalidAddress: 'Invalid contract address.',
  profileUnavailable: 'Token profile unavailable.',
  tradeOnNutrimatic: 'Trade on Nutri-Matic',
  noPriceData: 'No price data for this token.',
  liquidity: {
    noPools: (symbol: string) => `No pools associated with ${symbol}.`,
    viewEcosystemPools: 'View ecosystem pools',
    poolsWithSymbol: (symbol: string) => `Pools with ${symbol}`,
    shareInPool: (tvl: string, share: string) => `TVL ${tvl} · Share ${share}`,
    exploreEcosystem: 'Explore entire ecosystem',
  },
} as const

export const enBridge = {
  title: 'Bridge',
  subtitle: 'Cross-chain transfers',
  description:
    'Move assets between Infinite Drive and other supported networks using the official bridge.',
  chainRoute: 'Source chain → Destination chain',
  openBridgeSite: 'Open bridge',
  backToSwap: 'Back to Swap',
} as const

export const enProfile = {
  title: 'Profile',
  subtitle: 'Identity and wallet. Full history and overview in Portfolio.',
  wallet: { title: 'Wallet', description: 'Connected account details' },
  disconnect: 'Disconnect',
  resetHint: 'Button not responding?',
  resetConnection: 'Reset wallet connection',
  achievements: 'Achievements',
  achievement: {
    towelHolder: 'Towel holder',
    firstSwap: 'First swap',
    poolPioneer: 'Pool pioneer',
    bridgeCurious: 'Bridge curious',
  },
  connectPrompt: 'Connect your wallet to view your profile.',
  connectWallet: 'Connect Wallet',
} as const

export const enTradeSettings = {
  transactionTitle: 'Transaction settings',
  interfaceTitle: 'Interface settings',
  slippage: {
    label: 'Slippage tolerance',
    helper: 'Your transaction will revert if the price changes unfavorably by more than this percentage.',
    description:
      'Maximum gap you accept between the quoted price and the price at on-chain confirmation. If the market moves beyond this, the transaction reverts.',
    help: {
      introTitle: 'What is slippage?',
      cautionTitle: 'Before raising the value',
      caution:
        'A very wide margin accepts a final price worse than the quote shown—you may receive less than you expected. Reserve it for highly volatile markets.',
    },
    presetGuide: {
      title: 'Quick guide by scenario',
      p01: 'Very stable pairs (e.g. stablecoin ↔ stablecoin)',
      p05: 'Everyday use for most swaps',
      p1: 'Active markets or tokens with a wide spread',
      p25: 'High volatility, launches, or very busy pools',
      p5: 'Typical ceiling on this DEX; only for extreme situations',
      custom: 'You can also type any other value in the custom field.',
    },
    invalid: 'Enter a valid slippage percentage',
    customLabel: 'Custom',
    riskyLow: 'Slippage is very low: the swap may revert if the price moves slightly',
    riskyHigh:
      'Above 5%: the final price may drift from the quote and the outcome may be less favorable than you accepted.',
  },
  deadline: {
    label: 'Transaction deadline',
    helper: 'Your transaction will revert if it is pending for more than this long.',
    description:
      'Maximum time (in minutes) your transaction can stay pending in the mempool before it expires.',
    help: {
      introTitle: 'What is the deadline?',
    },
    examples:
      '20–30 minutes is usually enough. Very low values (e.g. 5 min) may cause failed swaps if the network is congested or you take time to sign.',
    minutes: 'minutes',
  },
  expertMode: {
    confirmTitle: 'Are you sure?',
    warning:
      'Expert mode turns off the confirm transaction prompt and allows high slippage trades that often result in bad rates and lost funds.',
    onlyIfYouKnow: 'ONLY USE THIS MODE IF YOU KNOW WHAT YOU ARE DOING.',
    turnOn: 'Turn on expert mode',
    promptConfirm: 'Please type the word "confirm" to enable expert mode.',
    toggle: 'Toggle expert mode',
    toggleHelper:
      'Bypasses confirmation modals and allows high slippage trades. Use at your own risk.',
  },
} as const
